import React, { useMemo, useState } from 'react';
import styles from './ManagerBasicData.module.scss';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import useBasicData from '../../../../Hooks/useBasicData';
import {
	Meta,
	useWorkplaceBasicDataQuery,
} from '../../../../../../graphql/graphql';
import Loading from '../../../../../../Components/Loading';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useMeta from '../../../../../Meta/Hooks/useMeta';
import { getBase64AsFile } from '../../../../../Base/Helpers/image.helper';
import {
	updateWorkplaceData,
	WorkplaceUpdateData,
} from '../../../../Services/workplaceUpdate.service';
import getTermItemId from '../../../../Helpers/getTermItemId';

interface Props {
	workplaceId: string;
}

const ManagerBasicData: React.FC<Props> = ({ workplaceId }) => {
	const { getMetaAsObject } = useMeta();
	const [isLoading, setIsLoading] = useState(false);
	const {
		data,
		isLoading: isLoadingBasicData,
		refetch,
	} = useWorkplaceBasicDataQuery({
		id: workplaceId,
	});
	const { formScheme } = useBasicData();
	const { displayError } = useApiClient();

	const defaultData = useMemo(() => {
		const workplace = data?.workplace;
		if (workplace) {
			const meta = getMetaAsObject(workplace.meta as Meta[]);

			return {
				...workplace,
				parentId: {
					label: workplace.parentWorkplace?.doctorDisplayName,
					value: workplace.parentWorkplace?.id,
				},
				fieldOfCare: workplace.termItems?.find(
					item => item?.term?.name === 'field_of_care',
				)?.id,
				formOfCare: workplace.termItems?.find(
					item => item?.term?.name === 'sukl_forma_pece',
				)?.id,
				icpNumber: meta['icp'],
				iczNumber: meta['icz'],
				workplaceP1Region: meta['workplace-p1-region'],
				workplaceP1Oid: meta['workplace-p1-oid'],
				workplaceP1Extension: meta['workplace-p1-extension'],
				useElectronicSignatureTimestamp:
					meta['useElectronicSignatureTimestamp'],
				patientProfileChangeNotification:
					meta['workplace_patient_profile_notification_settings']
						?.patientProfileChangeNotification ?? false,
				workplaceInsuranceCompanyBranch:
					meta['workplaceInsuranceCompanyBranch'],
			};
		}

		return undefined;
	}, [data]);

	const handleUploadImageToWorkplace = async (
		image: string,
		endpoint: string,
	) => {
		try {
			const file = await getBase64AsFile(image);
			if (file) {
				// @ts-ignore
				const formData = new FormData();
				formData.append('file', file);
				await apiClient.postMultipart(
					`/workplaces/${workplaceId}/${endpoint}`,
					formData,
				);
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const {
			meta,
			fieldOfCare,
			formOfCare,
			createCaseTarget,
			onlineOrderTarget,
			icpNumber,
			iczNumber,
			parentId,
			logoUrl,
			letterHeadPDFUrl,
			useElectronicSignatureTimestamp,
			patientProfileChangeNotification,
			workplaceInsuranceCompanyBranch,
			...restOfValues
		} = values;

		const termItems: { id: string }[] = [];
		const updateMeta: { key: string; value: string }[] = [];

		const fieldOfCareId = getTermItemId(fieldOfCare);
		const formOfCareId = getTermItemId(formOfCare);

		if (fieldOfCareId) {
			termItems.push({ id: fieldOfCareId });
		}

		if (formOfCareId) {
			termItems.push({ id: formOfCareId });
		}

		updateMeta.push({
			key: 'icp',
			value: icpNumber,
		});

		updateMeta.push({
			key: 'icz',
			value: iczNumber,
		});

		updateMeta.push({
			key: 'useElectronicSignatureTimestamp',
			value: useElectronicSignatureTimestamp ?? false,
		});

		updateMeta.push({
			key: 'workplace_patient_profile_notification_settings',
			value: patientProfileChangeNotification ?? false,
		});

		const data: WorkplaceUpdateData = {
			...restOfValues,
			termItems,
			meta: updateMeta,
			parentWorkplaceId: parentId.value,
			createCaseTarget: createCaseTarget?.value || createCaseTarget,
			onlineOrderTarget: onlineOrderTarget?.value || onlineOrderTarget,
			workplaceInsuranceCompanyBranch:
				workplaceInsuranceCompanyBranch?.value || null,
		};

		try {
			const response = await updateWorkplaceData(workplaceId, data);

			if (logoUrl) {
				await handleUploadImageToWorkplace(logoUrl, 'logo');
			}
			if (letterHeadPDFUrl) {
				await handleUploadImageToWorkplace(letterHeadPDFUrl, 'letter-head-pdf');
			}
			if (response.status === 200) {
				await refetch();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isLoading || isLoadingBasicData) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className='row'>
				<div className='col-12 col-md-6'>
					<MagicForm
						formScheme={formScheme}
						onSubmit={handleSubmit}
						defaultValues={defaultData}
					/>
				</div>
			</div>
		</div>
	);
};

export default ManagerBasicData;
